import axios from '../axiosInstance';

/**
 * Fetch all weighbridges (weighbridges table)
 * @returns {Promise<Array>} Array of weighbridge objects
 */
export async function getWeighbridges(signal = null) {
    const { data } = await axios.get('/Weighbridges', { signal });
    return data;
}

/**
 * Get weighbridge by ID
 * @param {number} id - Weighbridge ID
 * @returns {Promise<Object>} Weighbridge object
 */
export async function getWeighbridgeById(id, signal = null) {
    const { data } = await axios.get(`/Weighbridges/${id}`, { signal });
    return data;
}

/**
 * Create a weighbridge.
 * @param {{ description: string, hardwareCode?: string|null, channelNo?: string|null, minimumWeight?: number|null, maximumWeight?: number|null, siteId?: number|null }} payload
 * @returns {Promise<Object>}
 */
export async function createWeighbridge(payload, signal = null) {
    const { data } = await axios.post('/Weighbridges', payload, { signal });
    return data;
}

/**
 * Update weighbridge connection and limit fields.
 * @param {number} id
 * @param {{ description?: string|null, hardwareCode?: string|null, channelNo?: string|null, minimumWeight?: number|null, maximumWeight?: number|null, siteId?: number|null }} payload
 * @returns {Promise<void>}
 */
export async function updateWeighbridge(id, payload, signal = null) {
    await axios.put(`/Weighbridges/${id}`, payload, { signal });
}

/**
 * Delete a weighbridge record.
 * @param {number} id
 * @returns {Promise<void>}
 */
export async function deleteWeighbridge(id, signal = null) {
    await axios.delete(`/Weighbridges/${id}`, { signal });
}

/**
 * Read a live weight from the weighbridge indicator over IP (I400/BI400).
 * Backend uses WEIGHBRIDGES.HARDWARE_CODE + CHANNEL_NO.
 * @param {number} weighbridgeId
 * @returns {Promise<{ weighbridgeId: number, weight: number, serialNo: number|null, state: string, rawData: string, protocol: string|null }>}
 */
export async function getWeighbridgeWeight(weighbridgeId, signal = null) {
    const id = Number(weighbridgeId);
    if (!Number.isFinite(id) || id <= 0) {
        throw new Error('A valid weighbridge id is required for live weight capture.');
    }
    try {
        const { data } = await axios.get(`/Weighbridges/${id}/weight`, { signal });
        const weight = Number(data?.weight);
        if (!Number.isFinite(weight) || weight <= 0) {
            throw new Error('The scale returned an invalid weight.');
        }
        const serialNoRaw = data?.serialNo;
        const serialNo = serialNoRaw != null && serialNoRaw !== '' ? Number(serialNoRaw) : null;
        return {
            weighbridgeId: Number(data?.weighbridgeId) > 0 ? Number(data.weighbridgeId) : id,
            weight,
            serialNo: Number.isFinite(serialNo) ? serialNo : null,
            state: data?.state ?? 'Stable',
            rawData: data?.rawData ?? '',
            protocol: data?.protocol ?? null,
        };
    } catch (err) {
        const apiError = err?.response?.data?.error
            || err?.response?.data?.title
            || err?.response?.data?.detail;
        if (apiError) throw new Error(apiError);
        throw err;
    }
}

/**
 * Poll live remote-weight displays (up to 6 indicators).
 * @returns {Promise<Array<{
 *   weighbridgeId: number,
 *   description: string,
 *   weight: number,
 *   units: string,
 *   stable: boolean,
 *   inRange: boolean,
 *   isError: boolean,
 *   method: string,
 *   errorMessage?: string|null
 * }>>}
 */
export async function getLiveWeightDisplays(signal = null) {
    const { data } = await axios.get('/Weighbridges/live-display', { signal });
    return Array.isArray(data) ? data : [];
}

/**
 * Poll a single weighbridge live display (weighing console capture target).
 * @param {number} weighbridgeId
 */
export async function getLiveWeightDisplay(weighbridgeId, signal = null) {
    const id = Number(weighbridgeId);
    if (!Number.isFinite(id) || id <= 0) {
        throw new Error('A valid weighbridge id is required for live display.');
    }
    try {
        const { data } = await axios.get(`/Weighbridges/${id}/live-display`, { signal });
        return data;
    } catch (err) {
        const apiError = err?.response?.data?.error
            || err?.response?.data?.title
            || err?.response?.data?.detail;
        if (apiError) throw new Error(apiError);
        throw err;
    }
}
